import { gql } from "@apollo/client";
import { associatedRisk, documentDetail } from "../fragments";

export const ASSET_IMAGE_DOWNLOADED = gql`
  subscription assetImageDownloaded($assetHierarchyId: Int!) {
    assetImageDownloaded(assetHierarchyId: $assetHierarchyId) {
      id
      imageUrl
    }
  }
`;

export const GENERAL_INSURANCE_DATA_SAVED = gql`
  subscription generalInsuranceDataSaved($offerId: Int!, $offerSiteId: Int!) {
    generalInsuranceDataSaved(offerId: $offerId, offerSiteId: $offerSiteId) {
      offerId
      offerSiteId
      insuranceData {
        id
        siteId
        ownCasco
        ownRca
        ownRcaReadOnly
        ownCascoReadOnly
        financedInsuranceRca
        financedPeriodRca
        financedInsuranceCasco
        financedPeriodCasco
        financedInsuranceRti
        financedPeriodRti
      }
      isManual
    }
  }
`;

export const INSURANCE_SIMULATION_SAVED = gql`
  subscription insuranceSimulationSaved($insuranceSimulationId: Int!) {
    insuranceSimulationSaved(insuranceSimulationId: $insuranceSimulationId) {
      id
      finishSaving
      keepQuotations
      silent
    }
  }
`;

export const CONVENTIONS_QUOTATIONS_SAVED = gql`
  subscription conventionsQuotationsSaved($insuranceSimulationId: Int!) {
    conventionsQuotationsSaved(insuranceSimulationId: $insuranceSimulationId) {
      id
      silent
    }
  }
`;

export const QUOTATIONS_SAVED = gql`
  subscription quotationsSaved($insuranceSimulationId: Int!) {
    quotationsSaved(insuranceSimulationId: $insuranceSimulationId) {
      id
    }
  }
`;

export const QUOTATIONS_COMPUTED = gql`
  subscription quotationsComputed($insuranceSimulationId: Int!) {
    quotationsComputed(insuranceSimulationId: $insuranceSimulationId) {
      id
    }
  }
`;

export const RTI_OPTION_UPDATED = gql`
  subscription rtiOptionUpdated($insuranceSimulationId: Int!) {
    rtiOptionUpdated(insuranceSimulationId: $insuranceSimulationId) {
      id
      hasRtiOption
      rtiEligible
      rtiOption {
        insuranceSimulationId
        insuranceAmount
        financedInsurance
        financedPeriod
        periodicity
        insurerName
      }
    }
  }
`;

export const INSURANCE_QUOTATION_SELECTED = gql`
  subscription insuranceQuotationSelected($offerId: Int!, $offerSiteId: Int!, $insuranceSimulationId: Int!) {
    insuranceQuotationSelected(insuranceSimulationId: $insuranceSimulationId) {
      id
      selectedInsuranceQuotationId
      rtiEligible
      allowRtiOption(offerId: $offerId, offerSiteId: $offerSiteId)
      hasRtiOption
      selectedQuotation {
        id
        insurerId
        insurerName
        cascoQuotation {
          id
          insuranceAmount
          conventionInfo {
            conventionName
          }
        }
      }
    }
  }
`;

export const INSURANCE_QUOTATION_SELECTED_EVENT = gql`
  subscription insuranceQuotationSelectedEvent($insuranceSimulationId: Int!) {
    insuranceQuotationSelected(insuranceSimulationId: $insuranceSimulationId) {
      id
    }
  }
`;

export const CASCO_QUOTATION_UPDATED = gql`
  subscription cascoQuotationUpdated($cascoQuotationId: Int!) {
    cascoQuotationUpdated(cascoQuotationId: $cascoQuotationId) {
      quotation {
        id
        insuranceSimulationId
        insurerName
        period
        cascoQuotation {
          id
          insuranceAmount
          totalInsuranceAmount
          totalDamageDeductiblePercent
          partialDamageDeductibleAmount
          periodicity
          insuredAmount
          associatedRisks {
            ...associatedRisk
          }
          coefficients {
            additionalDataTypeId
            additionalDataName
            details {
              detailId
              detailName
              selected
            }
          }
        }
      }
      silentMode
    }
  }
  ${associatedRisk}
`;

export const LEASING_SIMULATION_BY_OFFER = gql`
  subscription leasingSimulationByOffer($offerId: Int!, $offerSiteId: Int!) {
    leasingSimulationByOffer(offerId: $offerId, offerSiteId: $offerSiteId) {
      documentId
      siteId
      insured {
        id
        name
        drivingLicenseDate
        partnerTypeId
        partnerTypeCode
        defaultActivityFieldId
        socialAddress {
          id
          districtId
          districtName
          cityId
          cityName
        }
      }
    }
  }
`;

export const RCA_QUOTATION_SELECTED = gql`
  subscription rcaQuotationSelected($insuranceSimulationId: Int!) {
    rcaQuotationSelected(insuranceSimulationId: $insuranceSimulationId) {
      id
      selectedInsuranceQuotationId
      selectedQuotation {
        id
        insurerId
        insurerName
        rcaQuotation {
          id
          selectedDetailId
          selectedDetail {
            id
            insuranceAmount
          }
        }
      }
    }
  }
`;

export const DOCUMENT_DETAILS_CHANGED = gql`
  subscription documentDetailsChanged($offerId: Int!, $offerSiteId: Int!) {
    documentDetailsChanged(offerId: $offerId, offerSiteId: $offerSiteId) {
      ...documentDetail
    }
  }
  ${documentDetail}
`;

export const LEASING_OFFER_CHANGED = gql`
  subscription leasingOfferChanged($documentId: Int!, $siteId: Int!) {
    leasingOfferChanged(documentId: $documentId, siteId: $siteId) {
      documentId
      siteId
      leasingSimulation {
        documentId
        siteId
        duration
        documentDetails {
          documentDetailId
          siteId
          insuranceSimulations {
            id
          }
        }
        insuranceData {
          id
          siteId
        }
      }
    }
  }
`;

export const INSURANCE_SIMULATIONS_INVALIDATED = gql`
  subscription insuranceSimulationsInvalidated($offerId: Int!, $offerSiteId: Int!) {
    insuranceSimulationsInvalidated(offerId: $offerId, offerSiteId: $offerSiteId) {
      offerId
      offerSiteId
      insuranceTypeNames
      insuranceSimulationIds
    }
  }
`;

export const REQUIRED_DOCUMENTS_EXISTENCE_CHECKED = gql`
  subscription requiredDocumentsExistenceChecked($insuranceSimulationId: Int!) {
    requiredDocumentsExistenceChecked(insuranceSimulationId: $insuranceSimulationId) {
      offerId
      offerSiteId
      clientId
      mandatoryRequiredDocuments
      optionalRequiredDocuments
    }
  }
`;

export const INSURANCE_OWNER_APPLICATION_CODE_UPDATED = gql`
  subscription insuranceOwnerApplicationCodeUpdated($insuranceSimulationId: Int!) {
    insuranceOwnerApplicationCodeUpdated(insuranceSimulationId: $insuranceSimulationId) {
      id
      ownerApplicationCode
    }
  }
`;

export const INSURANCE_FINANCING_OFFER_SAVED = gql`
  subscription insuranceFinancingOfferSaved($insuranceSimulationId: Int!, $insuranceTypeCode: String!) {
    insuranceFinancingOfferSaved(insuranceSimulationId: $insuranceSimulationId, insuranceTypeCode: $insuranceTypeCode) {
      id
      quotationsInvalidated
    }
  }
`;

export const INSURANCE_NEGOTIATION_REQUESTED = gql`
  subscription insuranceNegotiationRequested($insuranceSimulationId: Int!) {
    insuranceNegotiationRequested(insuranceSimulationId: $insuranceSimulationId) {
      insuranceSimulationId
    }
  }
`;

export const INSURANCE_NEGOTIATION_STARTED = gql`
  subscription insuranceNegotiationStarted($insuranceSimulationId: Int!) {
    insuranceNegotiationStarted(insuranceSimulationId: $insuranceSimulationId) {
      insuranceSimulationId
      message
    }
  }
`;

export const RCA_QUOTATION_UPDATED = gql`
  subscription rcaQuotationUpdated($insuranceSimulationId: Int!) {
    rcaQuotationUpdated(insuranceSimulationId: $insuranceSimulationId) {
      id
    }
  }
`;

export const DEFAULT_SUBCATEGORY_SELECTED = gql`
  subscription defaultSubcategorySelected($insuranceSimulationId: Int!) {
    defaultSubcategorySelected(insuranceSimulationId: $insuranceSimulationId) {
      id
      defaultSubcategoryCode
    }
  }
`;

export const LEASING_DOCUMENT_DETAIL_EQUIPMENT_SAVED = gql`
  subscription leasingDocumentDetailEquipmentSaved($offerId: Int!, $offerSiteId: Int!) {
    leasingDocumentDetailEquipmentSaved(offerId: $offerId, offerSiteId: $offerSiteId) {
      insuranceSimulationId
    }
  }
`;

export const OFFER_FINANCING_STRUCTURE_INVALIDATED = gql`
  subscription offerFinancingStructureInvalidated($offerId: Int!, $offerSiteId: Int!) {
    offerFinancingStructureInvalidated(offerId: $offerId, offerSiteId: $offerSiteId) {
      documentId
      siteId
    }
  }
`;

export const INSURANCE_SIMULATIONS_UPDATED = gql`
  subscription insuranceSimulationsUpdated($offerId: Int!, $offerSiteId: Int!) {
    insuranceSimulationsUpdated(offerId: $offerId, offerSiteId: $offerSiteId) {
      documentId
      siteId
      insuranceSimulationIds
    }
  }
`;

export const INSURANCE_QUOTATIONS_STATUS_CHANGED = gql`
  subscription insuranceQuotationsStatusChanged($insuranceSimulationId: Int!) {
    insuranceQuotationsStatusChanged(insuranceSimulationId: $insuranceSimulationId) {
      insuranceSimulationId
      quotationsStatus
    }
  }
`;

export const INSURANCE_STATE_CODE_UPDATED_FAILED = gql`
  subscription externalInsuranceStateCodeUpdateFailed($insuranceSimulationId: Int!) {
    externalInsuranceStateCodeUpdateFailed(insuranceSimulationId: $insuranceSimulationId) {
      insuranceSimulationId
      message
    }
  }
`;

export const ENTITY_TYPE_CHANGED = gql`
  subscription entityTypeChanged {
    entityTypeChanged {
      entityName
    }
  }
`;

export const POLICY_DOCUMENT_NUMBER_SAVED = gql`
  subscription policyDocumentNumberSaved($insuranceSimulationId: Int!) {
    policyDocumentNumberSaved(insuranceSimulationId: $insuranceSimulationId) {
      insuranceSimulationId
      policyDocumentNumber
    }
  }
`;
